import React from "react";
import { Plus, Lightbulb, PencilSimple } from "@phosphor-icons/react";
import InfoTip from "@/components/onboarding/InfoTip";
import CollapsibleCard from "@/components/onboarding/CollapsibleCard";
import MoneyInput from "@/components/MoneyInput";
import { useOpenCard } from "@/components/onboarding/useOpenCard";
import { monthNames } from "@/lib/costHelpers";
import { launchMonthIndex, isLaunch } from "@/lib/incomeHelpers";

export const GOAL_KINDS = [
  { v: "revenue", label: "Income £" },
  { v: "clients", label: "Clients / sales" },
  { v: "cash", label: "Cash buffer £" },
  { v: "launch", label: "Launch" },
  { v: "big_decision", label: "Big decision" },
  { v: "custom", label: "Custom" },
];
const kindLabel = (k) => (GOAL_KINDS.find((x) => x.v === k) || GOAL_KINDS[0]).label;
const Label = ({ children }) => <label className="block text-xs font-semibold mb-1">{children}</label>;
const goalComplete = (g) => !!(g.title || "").trim();

export function goalMonth(g, streams, startIso) {
  const months = monthNames(startIso, true);
  if (g.kind === "launch") {
    const s = streams.find((r) => r.id === g.linked_stream_id);
    return s ? months[launchMonthIndex(s)] : null;
  }
  return g.target_month_index ? months[Number(g.target_month_index) - 1] : null;
}

const LaunchFields = ({ g, i, streams, startIso, onPatch, onEditStream, onAddStream }) => {
  const named = streams.filter((r) => r.name);
  const sorted = [...named.filter(isLaunch), ...named.filter((r) => !isLaunch(r))];
  const linked = named.find((r) => r.id === g.linked_stream_id);
  return (
    <div className="md:col-span-2 space-y-3">
      <div>
        <Label>Which income stream is this?</Label>
        <select className="input" value={g.linked_stream_id || ""} onChange={(e) => onPatch({ linked_stream_id: e.target.value })} data-testid={`goal-stream-${i}`}>
          <option value="">Select…</option>
          {sorted.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
          <option value="__none">I haven't added it yet</option>
        </select>
      </div>
      {linked && (
        <p className="text-sm flex items-center gap-2" data-testid={`goal-launch-month-${i}`}>
          Launching <strong>{goalMonth(g, streams, startIso)}</strong> (from your income stream)
          <button type="button" className="btn-secondary !px-2 !py-1" onClick={() => onEditStream(linked.id)} data-testid={`goal-edit-stream-${i}`} aria-label="Edit income stream">
            <PencilSimple size={14} />
          </button>
        </p>
      )}
      {g.linked_stream_id === "__none" && (
        <div className="ai-insight text-sm" data-testid={`goal-launch-missing-${i}`}>
          Add it to your income streams so your forecast includes the money it could bring in.{" "}
          <button type="button" className="underline font-semibold" onClick={onAddStream} data-testid={`goal-add-stream-${i}`}>Take me to Income streams</button>
        </div>
      )}
    </div>
  );
};

const GoalFields = ({ g, i, streams, startIso, onPatch, onEditStream, onAddStream }) => {
  const months = monthNames(startIso, true);
  const big = g.kind === "big_decision";
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <Label>Your goal</Label>
        <input className="input" value={g.title} onChange={(e) => onPatch({ title: e.target.value })} placeholder="e.g. Hit £5k months by September" data-testid={`goal-title-${i}`} maxLength={200} />
      </div>
      <div className="md:col-span-2">
        <Label>Why does it matter to you? (optional)</Label>
        <input className="input" value={g.why || ""} onChange={(e) => onPatch({ why: e.target.value })} placeholder="e.g. So I can take Fridays off" data-testid={`goal-why-${i}`} maxLength={500} />
      </div>
      <div>
        <Label>
          Type
          <InfoTip testid={`goal-kind-tip-${i}`}>
            <ul className="list-disc pl-4 space-y-1">
              <li><strong>Income £:</strong> a sales target, e.g. "£5k months by September"</li>
              <li><strong>Clients / sales:</strong> a number to reach, e.g. "10 coaching clients by June"</li>
              <li><strong>Cash buffer £:</strong> a safety net in the bank, e.g. "3 months of costs saved by December"</li>
              <li><strong>Launch:</strong> bringing a new income stream to life</li>
              <li><strong>Big decision:</strong> something you're weighing up, e.g. hiring a VA or renting a studio. Your AI coach checks whether your forecast can carry it.</li>
              <li><strong>Custom:</strong> anything else that matters to you</li>
            </ul>
          </InfoTip>
        </Label>
        <select className="input" value={g.kind} onChange={(e) => onPatch({ kind: e.target.value })} data-testid={`goal-kind-${i}`}>
          {GOAL_KINDS.map((k) => (
            <option key={k.v} value={k.v}>{k.label}</option>
          ))}
        </select>
      </div>
      {g.kind === "custom" && (
        <div>
          <Label>What are you tracking?</Label>
          <input className="input" value={g.metric || ""} onChange={(e) => onPatch({ metric: e.target.value })} placeholder="e.g. Newsletter subscribers" data-testid={`goal-metric-${i}`} maxLength={200} />
        </div>
      )}
      {g.kind === "launch" ? (
        <LaunchFields g={g} i={i} streams={streams} startIso={startIso} onPatch={onPatch} onEditStream={onEditStream} onAddStream={onAddStream} />
      ) : (
        <>
          {big && (
            <div>
              <Label>Is the cost monthly or one-off?</Label>
              <select className="input" value={g.decision_cost_type || "monthly"} onChange={(e) => onPatch({ decision_cost_type: e.target.value })} data-testid={`goal-cost-type-${i}`}>
                <option value="monthly">Monthly cost</option>
                <option value="one_off">One-off cost</option>
              </select>
            </div>
          )}
          <div>
            <Label>{big ? `Cost (£${(g.decision_cost_type || "monthly") === "monthly" ? " per month" : ""}, optional)` : "Target (optional)"}</Label>
            {big || g.kind === "revenue" || g.kind === "cash" ? (
              <MoneyInput value={g.target_value} onChange={(n) => onPatch({ target_value: n })} testid={`goal-target-${i}`} placeholder={big ? "900" : "5,000"} />
            ) : (
              <input className="input" inputMode="numeric" value={g.target_value} onChange={(e) => onPatch({ target_value: e.target.value.replace(/[^\d.]/g, "") })} placeholder="e.g. 10" data-testid={`goal-target-${i}`} />
            )}
          </div>
          <div>
            <Label>
              {big ? "When would you like to make this happen? (optional)" : "By month"}
              {big && (
                <InfoTip testid={`goal-when-tip-${i}`}>Pick the month you'd ideally like to go ahead. It gives your AI coach a clearer picture when checking whether your forecast can afford it from then on.</InfoTip>
              )}
            </Label>
            <select className="input" value={g.target_month_index} onChange={(e) => onPatch({ target_month_index: e.target.value })} data-testid={`goal-month-${i}`}>
              <option value="">{big ? "No preference" : "No deadline"}</option>
              {months.map((m, k) => (
                <option key={k} value={k + 1}>{m}</option>
              ))}
            </select>
          </div>
        </>
      )}
    </div>
  );
};

export const GoalsStep = ({ goals, streams, startIso, makeItem, onAdd, onPatch, onRemove, onEditStream, onAddStream }) => {
  const card = useOpenCard(goals, goalComplete);
  return (
    <div className="space-y-5" data-testid="goals-step">
      <p className="text-black/80 leading-relaxed">This is where your forecast becomes more than numbers. What do you actually want to achieve in the next 12 months?</p>
      <p className="text-black/80 leading-relaxed">A forecast without a goal is just a spreadsheet. A goal without a forecast is just a wish. Together? That's where calm clarity lives.</p>
      <ul className="list-disc pl-5 space-y-1 text-black/80 leading-relaxed">
        <li>Add the goals that feel meaningful to you.</li>
        <li>Your AI coach will help turn each one into an action plan on your Goals page once this setup is complete.</li>
        <li>You can add or change goals any time from the Goals page.</li>
      </ul>
      <div className="card bg-[#FFF7E5] flex items-start gap-3">
        <Lightbulb size={20} weight="duotone" className="shrink-0 mt-0.5" />
        <p className="text-sm leading-relaxed">
          <strong>A tip from Magdalena:</strong> Building a business is a journey, not a sprint. We recommend setting <strong>2–3 goals per quarter</strong>: enough to keep momentum, few enough to actually action. Quality over quantity. You can always come back and add more once these are in flight.
        </p>
      </div>
      {goals.map((g, i) => {
        const month = goalMonth(g, streams, startIso);
        return (
          <CollapsibleCard key={g.id} testid={`goal-row-${i}`} title={[g.title || `Goal ${i + 1}`, kindLabel(g.kind), month].filter(Boolean).join(" · ")} open={card.openId === g.id} nudge={card.nudgeId === g.id ? "Give your goal a name first." : false} onToggle={() => card.toggle(g.id)} onRemove={() => onRemove(i)}>
            <GoalFields g={g} i={i} streams={streams} startIso={startIso} onPatch={(p) => onPatch(i, p)} onEditStream={onEditStream} onAddStream={onAddStream} />
          </CollapsibleCard>
        );
      })}
      <button type="button" className="btn-secondary" onClick={() => card.tryAdd(makeItem(), onAdd)} data-testid="goal-add">
        <Plus size={16} weight="bold" /> Add {goals.length ? "another" : "a"} goal
      </button>
    </div>
  );
};

export default GoalsStep;
